<i18n src="./resources/locales.json"></i18n>

<template>
  <EcsDashboardCard :disabled="disabled" :dark="$vuetify.theme.dark" @resize="expanded => $emit('resize', expanded)">
    <v-btn icon class="card-help-btn" @click="overlay = !overlay">
      <v-icon>mdi-help-circle</v-icon>
    </v-btn>

    <div class="title-container">
      <div v-once class="card-title">{{ $t('agentKPI') }}</div>
    </div>

    <div class="card-body">
      <div class="total-stat">
        <div class="total-stat-count">{{ totalAnsweredCalls }}</div>
        <div v-once class="total-stat-description">{{ $t('answeredCalls') }}</div>
      </div>

      <div class="total-stat">
        <div class="total-stat-count">{{ missedCalls }}</div>
        <div v-once class="total-stat-description">{{ $t('missedCalls') }}</div>
      </div>

      <div class="total-stat">
        <div class="total-stat-count">{{ rejectedCalls }}</div>
        <div v-once class="total-stat-description">{{ $t('rejectedCalls') }}</div>
      </div>

      <div class="clear"></div>

      <div class="total-stat">
        <div class="total-stat-count">{{ totalTransfer }}</div>
        <div v-once class="total-stat-description">{{ $t('transferredCalls') }}</div>
      </div>

      <div class="total-stat">
        <div class="total-stat-count">
          {{ totalCallbacksRescheduled }}
        </div>
        <div v-once class="total-stat-description">{{ $t('callbacksHandled') }}</div>
      </div>

      <div class="total-stat">
        <div class="total-stat-count">
          {{ kpiFormat((loggedInAgentStats.agentAverageTreatingTime || {}).value, 'duration', true) }}
        </div>
        <div v-once class="total-stat-description">{{ $t('averageHandleTime') }}</div>
      </div>

      <div class="clear"></div>

      <EcsAgentKPICardStat
        :label="$t('loginDuration')"
        :value="timers.onlineTime"
        :is-loading="isWaitingForLoginTimeCounter"
        :is-live="agentState.isOnline"
      />

      <EcsAgentKPICardStat
        :label="$t('readyDuration')"
        :value="timers.readyTime"
        :is-loading="isWaitingForReadyCounter"
        :is-live="agentState.isReady"
      />

      <EcsAgentKPICardStat
        :label="$t('breakDuration')"
        :value="timers.breakTime"
        :is-loading="isWaitingForBreakCounter"
        :is-live="agentState.isBreak"
      />

      <div class="clear"></div>

      <EcsAgentKPICardStat
        :label="$t('busyDuration')"
        :value="timers.busyTime"
        :is-loading="isWaitingForBusyCounter"
        :is-live="agentState.isBusy"
      />

      <EcsAgentKPICardStat
        :label="$t('backofficeTimeInShiftDuration')"
        :value="timers.backofficeTime"
        :is-loading="isWaitingForBackofficeCounter"
        :is-live="agentState.isBackoffice"
      />

      <EcsAgentKPICardStat
        :label="$t('outgoingTimeInShiftDuration')"
        :value="timers.outgoingTime"
        :is-loading="isWaitingForOutgoingCounter"
        :is-live="agentState.isOutgoing"
      />

      <div class="clear card-footer"></div>
    </div>

    <v-fade-transition>
      <div v-if="overlay" class="help-overlay" @click="overlay = false">
        <div class="help-content">
          <v-btn icon small class="close-help-btn" @click="overlay = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <h2 v-once>{{ $t('agentKPI') }}</h2>
          <div class="help-items-container custom-scrollbar">
            <div v-for="(item, index) in helpItems" :key="index">
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </v-fade-transition>
  </EcsDashboardCard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import EcsDashboardCard from '@/components/ecs-dashboard-card/EcsDashboardCard';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';
import EcsAgentStatsMath from '@/components/ecs-agent-stats/EcsAgentStats.math';
import { agentSubscriptions } from '@/constants/kpi-counters/subscriptions';
import EcsAgentKPICardStat from '@/components/ecs-agent-kpi-card/EcsAgentKPICardStat.vue';
import StatisticsService from '@/services/statisticsService';

export default {
  name: 'EcsAgentKPICard',

  components: {
    EcsDashboardCard,
    EcsAgentKPICardStat
  },

  mixins: [EcsKpiFormatter],

  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      overlay: false,
      expanded: false,
      timers: {
        onlineTime: '',
        readyTime: '',
        breakTime: '',
        busyTime: '',
        backofficeTime: '',
        outgoingTime: ''
      },
      liveTimersUpdateRef: null,
      helpItems: [
        {
          title: this.$t('helpItems.interactions.title'),
          description: this.$t('helpItems.interactions.description')
        },
        {
          title: this.$t('helpItems.avgHandleTime.title'),
          description: this.$t('helpItems.avgHandleTime.description')
        },
        {
          title: this.$t('helpItems.loginTimeSpan.title'),
          description: this.$t('helpItems.loginTimeSpan.description')
        },
        {
          title: this.$t('helpItems.ready.title'),
          description: this.$t('helpItems.ready.description')
        },
        {
          title: this.$t('helpItems.break.title'),
          description: this.$t('helpItems.break.description')
        },
        {
          title: this.$t('helpItems.busy.title'),
          description: this.$t('helpItems.busy.description')
        },
        {
          title: this.$t('helpItems.backoffice.title'),
          description: this.$t('helpItems.backoffice.description')
        },
        {
          title: this.$t('helpItems.outgoing.title'),
          description: this.$t('helpItems.outgoing.description')
        }
      ]
    };
  },

  computed: {
    ...mapGetters('AGENT', ['agentState', 'loggedInAgent']),

    ...mapGetters('STATS', [
      'agentsLastUpdate',
      'bpsLastUpdate',
      'loggedInAgentStats',
      'lastKnownAgentStateChange',
      'getSubscribedCounters'
    ]),

    ...mapGetters('EVENT_TIMING', ['isWaitingForKpiCounter']),

    totalAnsweredCalls() {
      return (this.loggedInAgentStats.agentTotalAnsweredCalls || {}).value;
    },

    missedCalls() {
      return (this.loggedInAgentStats.agentMissedCalls || {}).value;
    },

    rejectedCalls() {
      return (this.loggedInAgentStats.agentRejectedCalls || {}).value;
    },

    totalTransfer() {
      return (this.loggedInAgentStats.agentTotalTransfer || {}).value;
    },

    totalCallbacksRescheduled() {
      return (this.loggedInAgentStats.agentTotalCallbacksReschedule || {}).value;
    },

    isWaitingForLoginTimeCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.loginTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.loginTime.counterTypeId][this.loggedInAgent.id]
      );
    },

    isWaitingForReadyCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.readyTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.readyTime.counterTypeId][this.loggedInAgent.id]
      );
    },

    isWaitingForBreakCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.breakTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.breakTime.counterTypeId][this.loggedInAgent.id]
      );
    },

    isWaitingForBusyCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.busyTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.busyTime.counterTypeId][this.loggedInAgent.id]
      );
    },

    isWaitingForBackofficeCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.backofficeTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.backofficeTime.counterTypeId][this.loggedInAgent.id]
      );
    },

    isWaitingForOutgoingCounter() {
      return (
        this.isWaitingForKpiCounter[agentSubscriptions.outgoingTime.counterTypeId] &&
        this.isWaitingForKpiCounter[agentSubscriptions.outgoingTime.counterTypeId][this.loggedInAgent.id]
      );
    }
  },

  created() {
    this.liveTimersUpdateRef = setInterval(this.updateTimers, 1000);

    Object.values(agentSubscriptions).forEach(subscription => {
      const payload = {
        counterTypeId: subscription.counterTypeId,
        handlerId: this.loggedInAgent.id
      };
      this.addEventTimingConstraintForKPICounter(payload);
      this.addKpiSubscription(payload);
    });
  },

  beforeDestroy() {
    clearInterval(this.liveTimersUpdateRef);
    this.clearLastKnownAgentStateChangeTimestamps();
    if (this.loggedInAgent) {
      StatisticsService.subscribeForCounters({
        clientSubscriptionId: this.loggedInAgent.id,
        handlerCounterPairs: []
      });
    }
  },

  methods: {
    ...mapActions('STATS', ['addKpiSubscription', 'clearLastKnownAgentStateChangeTimestamps']),

    ...mapActions('EVENT_TIMING', ['addEventTimingConstraintForKPICounter']),

    diffFormNowDate(dateTime) {
      const duration = dateTime && moment.duration(moment().diff(moment.utc(dateTime, 'MM/DD/YYYY HH:mm:ss')));

      return this.kpiFormat(
        duration && `${duration.hours()}:${duration.minutes()}:${duration.seconds()}`,
        'duration',
        true
      );
    },

    diffFormNow(dateTime, baseDuration = '00:00:00') {
      const durationA = dateTime && moment.duration(moment().diff(dateTime));
      const durationB = moment.duration(baseDuration);
      const finalDuration = dateTime ? EcsAgentStatsMath.sumDurations([durationA, durationB]) : durationB;

      return this.kpiFormat(
        `${finalDuration.hours()}:${finalDuration.minutes()}:${finalDuration.seconds()}`,
        'duration',
        true
      );
    },

    updateTimers() {
      const handlerId = this.loggedInAgent.id;
      const counter = {
        ready: this.getSubscribedCounters[agentSubscriptions.readyTime.counterTypeId][handlerId],
        break: this.getSubscribedCounters[agentSubscriptions.breakTime.counterTypeId][handlerId],
        busy: this.getSubscribedCounters[agentSubscriptions.busyTime.counterTypeId][handlerId],
        backoffice: this.getSubscribedCounters[agentSubscriptions.backofficeTime.counterTypeId][handlerId],
        outgoing: this.getSubscribedCounters[agentSubscriptions.outgoingTime.counterTypeId][handlerId]
      };

      const getDurationFromCounter = counter => {
        if (counter.secondaryValue && counter.secondaryValue !== '00:00:00') {
          if (counter.value && counter.value !== '00:00:00') {
            return moment.duration(counter.value) > moment.duration(counter.secondaryValue)
              ? counter.value
              : counter.secondaryValue;
          }

          return counter.secondaryValue;
        }

        return counter.value || '00:00:00';
      };

      this.timers = {
        onlineTime: this.agentState.isOnline ? this.diffFormNowDate(this.lastKnownAgentStateChange.online) : '0s',
        readyTime: this.diffFormNow(
          this.agentState.isReady ? this.lastKnownAgentStateChange.ready : null,
          getDurationFromCounter(counter.ready)
        ),
        breakTime: this.diffFormNow(
          this.agentState.isBreak ? this.lastKnownAgentStateChange.break : null,
          getDurationFromCounter(counter.break)
        ),
        busyTime: this.diffFormNow(
          this.agentState.isBusy ? this.lastKnownAgentStateChange.busy : null,
          getDurationFromCounter(counter.busy)
        ),
        backofficeTime: this.diffFormNow(
          this.agentState.isBackoffice ? this.lastKnownAgentStateChange.backoffice : null,
          getDurationFromCounter(counter.backoffice)
        ),
        outgoingTime: this.diffFormNow(
          this.agentState.isOutgoing ? this.lastKnownAgentStateChange.outgoing : null,
          getDurationFromCounter(counter.outgoing)
        )
      };
    }
  }
};
</script>

<style src="./scoped.css" scoped></style>
